import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Box,
  Chip,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { LocationStable } from '../../models/locations';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainMortalityReasonsMortalityReasonViewModel,
  HealthSystemDALModelDiseasesStableColour
} from '../../api/HealthSystemApi';
import CreateMortalityReasonModal from '../CreateMortalityReasonModal';

const FarmMortalityReasons: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();

  const [mortalityReasons, setMortalityReasons] = React.useState<
    HealthSystemDomainMortalityReasonsMortalityReasonViewModel[]
  >([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [createMortalityReasonModalOpened, setCreateMortalityReasonModalOpened] = React.useState(false);

  const loadFarmMortalityReasons = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.mortalityReasons
      .mortalityReasonsMortalityReasonsSearch({
        Page: searchPage,
        Size: rowsPerPage
      })
      .then((result) => {
        setMortalityReasons(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmMortalityReasons();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const handleRemove = (mortalityReason: HealthSystemDomainMortalityReasonsMortalityReasonViewModel) => {
    confirmDialog({
      dialogId: 'delete-mortality-reason-dialog',
      title: t('f-mortality-reasons-del'),
      message: t('f-mortality-reasons-del-confirm', { name: mortalityReason.name }),
      alertSeverity: 'warning'
    }).then(() => {
      apiService.api.mortalityReasons
        .mortalityReasonsRemoveMortalityReason({ id: mortalityReason.id })
        .then(() => {
          notifications.success(t('f-mortality-reasons-del-ok'));
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleModalClose = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setCreateMortalityReasonModalOpened(false);
  };

  const renderStableColors = (array: HealthSystemDALModelDiseasesStableColour[]) => {
    if (!array?.length) {
      return null;
    }

    return array.map((value, index) => (
      <Box key={value.stable} sx={{ display: 'flex', mt: index < 1 ? 0 : 1 }}>
        <Box sx={{ height: 20, width: 20, borderRadius: 3, bgcolor: value.colour }} />
        <Box sx={{ pl: 1 }}>
          <Typography variant={'caption'}>{LocationStable[value.stable]}</Typography>
        </Box>
      </Box>
    ));
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          {/* title & spinner */}
          <Grid container direction="row" sx={styles.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'sick'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-com-mortality-reasons')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container sx={styles.justifyRight} md={6} xs={12}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Tooltip title={t('f-com-create-new')} arrow={true}>
                  <IconButton
                    disabled={state.role === UserRole.user}
                    id="createSupplyBtn"
                    sx={{ ...V4_ICON_BUTTON_SMALL, ...styles.withMarginLeft }}
                    color="primary"
                    size="small"
                    onClick={() => setCreateMortalityReasonModalOpened(true)}
                  >
                    <Icon>{'add_circle'}</Icon>
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TableContainer component={Paper} elevation={1} sx={styles.mt2}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('f-com-name')}</TableCell>
                        <TableCell>{t('f-com-colour')}</TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {mortalityReasons.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{renderStableColors(row.stableColours)}</TableCell>
                          <TableCell>
                            <Tooltip arrow title={t('f-mortality-reasons-del')}>
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => handleRemove(row)}
                                sx={V4_ICON_BUTTON_SMALL}
                              >
                                <Icon>{'delete'}</Icon>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                          colSpan={6}
                          count={total}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateMortalityReasonModal opened={createMortalityReasonModalOpened} onModalAction={handleModalClose} />
    </>
  );
};

export default FarmMortalityReasons;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    mt2: {
      marginTop: theme.spacing(2)
    }
  }) as const;
